import { useEffect } from 'react';
import { Table, TableHead, TableHeader, TableBody, TableRow, TableCell } from '@clay-run/glaze/table';
import { Badge } from '@clay-run/glaze/badge';
import { Icon } from '@clay-run/glaze/icon';
import { Text } from '@clay-run/glaze/text';
import { Tooltip, TooltipPanel, TooltipTrigger } from '@clay-run/glaze/tooltip';
import clsx from 'clsx';
import type { AccountDealSummary } from '@shared/flow/account-hierarchy-deal-model';
import type { HierarchyRow } from '@shared/flow/account-hierarchy-tree';
import { renewalDaysOf, whitespaceStatusOf } from '@shared/flow/account-hierarchy-whitespace';
import { FLOW_ICONS } from '../../icons';
import type { DealSort, DealSortKey } from './account-hierarchy-view-store';
import type { HierarchyLens } from './graph/HierarchyGraphContext';
import { RenewalBadge } from './RenewalBadge';
import { SeparateBuyingCenterIndicator } from './SeparateBuyingCenterIndicator';
import { HierarchyNodeMenu, type HierarchyNodeActions } from './HierarchyNodeMenu';
import {
  DASH, DEAL_COLUMNS, HIERARCHY_COLUMNS, HIERARCHY_COPY, STATUS_COLUMN, WHITESPACE_LABEL, WHITESPACE_TONE, contactsLabel, dealAmountText, dealMoney,
  hierarchyMoney, hierarchyNum,
} from './hierarchyPresentation';

export interface HierarchyTableProps {
  rows: HierarchyRow[];
  currentId: string;
  selectedId: string;
  actions: HierarchyNodeActions;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onReveal: (parentId: string) => void;
  /**
   * The deal overlay: adds Deals / Deal amount / Deal ARR columns (each account's OWN matching
   * deals, no subtree roll-up) and dims rows with no matching deal. Null/absent = off.
   */
  dealSummaries?: ReadonlyMap<string, AccountDealSummary> | null;
  /**
   * Color by / Renewals due: a Status column (dot + label) while Color by is on, the Renews in Nd
   * badge on accounts renewing soon, and rows outside the highlighted set dimmed. Absent = off.
   */
  lens?: HierarchyLens | null;
  /** Accounts with any deal (any status, any filter): their menu offers "View deals". */
  dealAccountIds?: ReadonlySet<string>;
  /** Sort by a Deal column (siblings only: a row never leaves its parent). Header click cycles it. */
  sort?: DealSort | null;
  onSortChange?: (key: DealSortKey) => void;
  /** "Show in table": select-and-scroll to this row, then report it handled. */
  focusRequest?: { id: string; nonce: number } | null;
  onFocusHandled?: () => void;
}

const BASE_COL_COUNT = HIERARCHY_COLUMNS.length + 1; // + trailing menu column
const INDENT_PX = 16;

export function HierarchyTable({ rows, currentId, selectedId, actions, onSelect, onToggle, onReveal, dealSummaries = null, lens = null, dealAccountIds, sort = null, onSortChange, focusRequest = null, onFocusHandled }: HierarchyTableProps) {
  useEffect(() => {
    if (!focusRequest) return;
    const row = Array.from(document.querySelectorAll('[data-hierarchy-node-id]'))
      .find(el => el.getAttribute('data-hierarchy-node-id') === focusRequest.id);
    row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    onFocusHandled?.();
  }, [focusRequest, onFocusHandled]);

  const colCount = BASE_COL_COUNT + (dealSummaries ? DEAL_COLUMNS.length : 0) + 1; // + the always-shown Status column
  return (
    <Table dense size="xs" data-testid="hierarchy-table">
      <TableHead>
        <TableRow>
          {HIERARCHY_COLUMNS.flatMap(col => (col.key === 'owner'
            ? [<TableHeader key={STATUS_COLUMN.key}>{STATUS_COLUMN.label}</TableHeader>, <TableHeader key={col.key} align={col.align}>{col.label}</TableHeader>]
            : [<TableHeader key={col.key} align={col.align}>{col.label}</TableHeader>]))}
          {dealSummaries && DEAL_COLUMNS.map(col => {
            const sortKey = col.key === 'dealAmount' || col.key === 'dealArr' ? col.key : null;
            const sorted = sortKey !== null && sort?.key === sortKey ? sort.dir : null;
            return (
              <TableHeader
                key={col.key}
                align={col.align}
                aria-sort={sorted === 'desc' ? 'descending' : sorted === 'asc' ? 'ascending' : sortKey ? 'none' : undefined}
              >
                {col.help ? (
                  <Tooltip>
                    <TooltipTrigger>
                      {sortKey && onSortChange ? (
                        <button
                          type="button"
                          data-testid={`header-help-${col.key}`}
                          aria-label={`Sort by ${col.label}`}
                          onClick={() => onSortChange(sortKey)}
                          className="inline-flex items-center gap-1 hover:underline"
                        >
                          {col.label}
                          {sorted && <Icon type={sorted === 'desc' ? FLOW_ICONS.expanded : FLOW_ICONS.collapse} size="xs" aria-hidden />}
                        </button>
                      ) : (
                        <span tabIndex={0} data-testid={`header-help-${col.key}`}>{col.label}</span>
                      )}
                    </TooltipTrigger>
                    <TooltipPanel>{col.help}</TooltipPanel>
                  </Tooltip>
                ) : col.label}
              </TableHeader>
            );
          })}
          <TableHeader><span className="sr-only">Actions</span></TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(row => {
          if (row.kind === 'more') {
            return (
              <TableRow key={`more-${row.parentId}`} data-hierarchy-more-for={row.parentId}>
                <TableCell colSpan={colCount}>
                  <button
                    type="button"
                    onClick={() => onReveal(row.parentId)}
                    className="text-content-action hover:underline"
                    style={{ paddingLeft: row.depth * INDENT_PX }}
                  >
                    Show {row.hiddenCount} more
                  </button>
                </TableCell>
              </TableRow>
            );
          }
          const { node, depth, hasChildren, expanded } = row;
          const isCurrent = node.id === currentId;
          const label = node.name ?? node.id;
          const deals = dealSummaries?.get(node.id);
          const dimmed = lens?.highlightedIds ? !lens.highlightedIds.has(node.id) : Boolean(dealSummaries) && !deals;
          const renewalDays = lens ? renewalDaysOf(node.enterprise_renewal_date, lens.today) : null;
          const status = whitespaceStatusOf(node);
          return (
            <TableRow
              key={node.id}
              data-hierarchy-node-id={node.id}
              aria-selected={selectedId === node.id}
              tabIndex={0}
              className={clsx('cursor-pointer', selectedId === node.id && 'bg-bg-secondary', dimmed && 'opacity-60')}
              onClick={() => onSelect(node.id)}
              onDoubleClick={() => { if (!isCurrent) actions.onOpenAccount(node.id); }}
              onKeyDown={e => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' && !isCurrent) actions.onOpenAccount(node.id);
                if (e.key === ' ') { e.preventDefault(); onSelect(node.id); }
              }}
            >
              <TableCell>
                <div className="flex items-center gap-1" style={{ paddingLeft: depth * INDENT_PX }}>
                  {hasChildren ? (
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
                      onClick={e => { e.stopPropagation(); onToggle(node.id); }}
                      className="flex size-5 shrink-0 items-center justify-center rounded text-content-secondary hover:bg-bg-primary-hover"
                    >
                      <Icon type={expanded ? FLOW_ICONS.expanded : FLOW_ICONS.expand} size="xs" aria-hidden />
                    </button>
                  ) : (
                    <span className="size-5 shrink-0" aria-hidden />
                  )}
                  <span className="font-medium text-content-primary">{label}</span>
                  {isCurrent && <Badge size="xs" color="blue">{HIERARCHY_COPY.current}</Badge>}
                  {renewalDays !== null && <RenewalBadge node={node} days={renewalDays} />}
                  {node.parent_missing && (
                    <Text size="xs" variant="tertiary">{HIERARCHY_COPY.parentMissing}</Text>
                  )}
                </div>
              </TableCell>
              <TableCell>{node.account_type ?? DASH}</TableCell>
              <TableCell align="right">{hierarchyNum(node.number_of_employees)}</TableCell>
              <TableCell align="right">{hierarchyMoney(node.arr_active_subscriptions)}</TableCell>
              <TableCell align="right">{hierarchyMoney(node.open_pipeline)}</TableCell>
              <TableCell>{node.region ?? DASH}</TableCell>
              <TableCell align="right">{contactsLabel(node)}</TableCell>
              <TableCell align="center"><SeparateBuyingCenterIndicator node={node} compact /></TableCell>
              <TableCell>
                <span className="flex items-center gap-1.5" data-testid={`status-cell-${node.id}`}>
                  <span aria-hidden className={clsx('size-2.5 shrink-0 rounded-full', WHITESPACE_TONE[status].dot)} />
                  {WHITESPACE_LABEL[status]}
                </span>
              </TableCell>
              <TableCell>{node.owner_name ?? DASH}</TableCell>
              {dealSummaries && (
                <>
                  <TableCell align="right">{deals ? deals.count : DASH}</TableCell>
                  <TableCell align="right">{deals ? dealAmountText(deals.amount) : DASH}</TableCell>
                  <TableCell align="right">{deals ? dealMoney(deals.arr) : DASH}</TableCell>
                </>
              )}
              <TableCell>
                <HierarchyNodeMenu
                  accountId={node.id}
                  accountName={node.name}
                  isCurrent={isCurrent}
                  hasChildren={hasChildren}
                  hasDeals={dealAccountIds?.has(node.id) ?? false}
                  childrenExpanded={expanded}
                  actions={actions}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
